import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Message } from "primereact/message";
import {
  useGetBenefitReviewQueueQuery,
  useGetBenefitVerdictAccuracyQuery,
} from "store/queries/benefitComparison";
import {
  IComparisonFilters,
  IReviewQueueCluster,
  IVerdictAccuracyCell,
} from "types/BenefitComparison/BenefitComparison";

import {
  ACCURACY_METHOD_LABELS,
  COMPARISON_BENEFIT_LABELS,
  methodAccuracyText,
  VERDICT_QUESTION,
} from "./benefitComparisonWording";
import { READ_FROM_LABELS } from "./remitAccuracyWording";

const clusterNameBody = (row: IReviewQueueCluster) => (
  <div className="flex flex-column gap-1">
    <span className="font-semibold">
      {row.accountName} · {row.payerName}
    </span>
    <span className="text-color-secondary">
      {row.planName ?? "No plan name"}
    </span>
  </div>
);

const openBody = (onOpenCluster: (cluster: IReviewQueueCluster) => void) =>
  function ReviewButton(row: IReviewQueueCluster) {
    return (
      <Button
        type="button"
        label="Review"
        className="p-button-text p-button-sm"
        data-testid="benefitReviewQueue.open"
        onClick={() => onOpenCluster(row)}
      />
    );
  };

/**
 * CLE-2820 PR 4 — THE REVIEW QUEUE: every disagreement nobody has judged yet, grouped by the same
 * clusters the tab uses, so one review informs the checks sitting beside it.
 *
 * Under it, per-method accuracy measured against the verdicts — the figure this whole feature exists
 * to produce for accounts no remit ever reaches. 🚨 Every percentage carries its sample size.
 */
export function BenefitReviewQueue({
  filters,
  onOpenCluster,
}: {
  filters: IComparisonFilters;
  onOpenCluster: (cluster: IReviewQueueCluster) => void;
}) {
  const { data, isLoading, isError } = useGetBenefitReviewQueueQuery(filters);
  const { data: accuracyData } = useGetBenefitVerdictAccuracyQuery(filters);
  const queue = data?.data;
  const accuracy = accuracyData?.data;

  const methodBody = (method: IVerdictAccuracyCell["methods"][number]) => (
    <span className="white-space-nowrap">{methodAccuracyText(method)}</span>
  );

  return (
    <div data-testid="benefitReviewQueue">
      <div className="flex flex-column gap-2 mb-3">
        <h3 className="m-0">Review queue</h3>
        <span className="text-color-secondary">
          Checks where at least two of legacy, the engine and the AI picked
          different values and nobody has recorded a verdict yet. The question
          every reviewer answers: <em>{VERDICT_QUESTION}</em>
        </span>
      </div>

      {isError && (
        <Message
          severity="error"
          text="The review queue could not be loaded."
        />
      )}

      {queue && (
        <div
          className="flex flex-wrap gap-4 mb-3 text-color-secondary"
          data-testid="benefitReviewQueue.totals"
        >
          <span>
            <strong>{queue.totals.needingReview.toLocaleString()}</strong> to
            review
          </span>
          <span>
            {queue.totals.reviewed.toLocaleString()} of{" "}
            {queue.totals.disagreements.toLocaleString()} disagreements judged
          </span>
          {queue.totals.remitConflicts > 0 && (
            <span data-testid="benefitReviewQueue.remitConflicts">
              ⚠️ {queue.totals.remitConflicts.toLocaleString()} where a remit
              disagrees with the verdict — a remit outranks a verdict, and both
              are kept
            </span>
          )}
        </div>
      )}

      <DataTable
        value={queue?.clusters ?? []}
        loading={isLoading}
        emptyMessage="Nothing left to review in this window."
        data-testid="benefitReviewQueue.clusters"
      >
        <Column header="Account · Payer · Plan" body={clusterNameBody} />
        <Column
          header="Benefit"
          body={(row: IReviewQueueCluster) =>
            COMPARISON_BENEFIT_LABELS[row.benefit]
          }
        />
        <Column
          header="To review"
          body={(row: IReviewQueueCluster) => row.unreviewed}
        />
        <Column
          header="Only engine differs"
          body={(row: IReviewQueueCluster) => row.onlyEngineDiffers}
        />
        <Column
          header="Engine read from"
          body={(row: IReviewQueueCluster) =>
            READ_FROM_LABELS[row.readFrom] ?? row.readFrom
          }
        />
        <Column header="" body={openBody(onOpenCluster)} />
      </DataTable>

      <div className="flex flex-column gap-2 mt-5 mb-3">
        <h3 className="m-0">Verified accuracy, from verdicts</h3>
        <span className="text-color-secondary">
          How often each method matched the human verdict, over the verdicts in
          this window. Every figure carries its sample size. Verdicts of{" "}
          <em>can&apos;t tell</em> are counted and never scored.
        </span>
      </div>
      <DataTable
        value={accuracy?.benefits ?? []}
        emptyMessage="No verdicts yet."
        data-testid="benefitReviewQueue.accuracy"
      >
        <Column
          header="Benefit"
          body={(row: IVerdictAccuracyCell) =>
            COMPARISON_BENEFIT_LABELS[row.benefit]
          }
        />
        <Column
          header="Verdicts"
          body={(row: IVerdictAccuracyCell) =>
            `${row.verdicts}${
              row.cannotDetermine ? ` · ${row.cannotDetermine} can’t tell` : ""
            }`
          }
        />
        {(["legacy", "engineAtCheck", "engineToday", "ai"] as const).map(
          (method) => (
            <Column
              key={method}
              header={ACCURACY_METHOD_LABELS[method]}
              body={(row: IVerdictAccuracyCell) => {
                const entry = row.methods.find(
                  (each) => each.method === method
                );
                return entry ? methodBody(entry) : null;
              }}
            />
          )
        )}
        <Column
          header="Remit conflicts"
          body={(row: IVerdictAccuracyCell) => row.remitConflicts}
        />
      </DataTable>
    </div>
  );
}
